import { PdfGen, PdfSize, PdfRect, PdfBrush, PdfColor, PdfUnit, ETextAlign } from '../../pdf/pdf';
import { ILayoutListGen } from '../layoutList';
import { OptsList } from '../optsList';
import { GUtil } from '../../pdf/gutil';
import { GCalc } from '../../pdf/gcalc';
import { calcOrigin } from '../../core/genHelper';
import { urlQr } from '../../core/urlQr';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/barcode';
import '../../pdf/draw/helpers';
import '../../pdf/draw/shapes';
import { IItemCount, SITE_URL } from '@educraft/base';

const BLUE_COLOR = PdfColor.fromRgb(0, 0, 255);

function genId(rnd: { next(a: number, b: number): number }): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 4 }, () => chars[rnd.next(0, chars.length)]).join('');
}

function removeDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function getItems(opts: OptsList): { key: string; value: string }[] {
  return opts.items ?? [];
}

// ---------------------------------------------------------------------------
// Strip layout (QrBase equivalent)
// 5 items per page; value in left 75%, QR centered in right 25%
// ---------------------------------------------------------------------------
function drawStrip(
  pdf: PdfGen,
  items: { key: string; value: string }[],
  hasKey: boolean,
  hasStartStop: boolean,
  getQrData: (item: { key: string; value: string }, idx: number, total: number) => object,
  siteUrl?: string,
  getString?: (item: { key: string; value: string }) => string
): void {
  const margin1 = PdfUnit.FromMillimeters(5);
  const margin2 = PdfUnit.FromMillimeters(2);
  const perPage = 5;
  const { origin, size } = calcOrigin(1, perPage, margin1, PdfSize.A4);
  const total = items.length;

  // Pre-compute font from all values
  let currentPage: ReturnType<typeof pdf.addPage> | null = null;
  let f = GUtil.baseFont;

  for (let i = 0; i < total; i++) {
    const pos = i % perPage;
    if (pos === 0) {
      currentPage = pdf.addPage();
      f = currentPage.adjustFont(
        (size.width / 4) * 3,
        items.map((it) => it.value),
        GUtil.baseFont
      );
    }
    const p = currentPage!;

    const rect = new PdfRect(origin.x, origin.y + pos * size.height, size.width, size.height);
    const r1 = new PdfRect(rect.x, rect.y, (rect.width / 4) * 3, rect.height);
    const r2 = new PdfRect(r1.right, rect.y, rect.width / 4, rect.height);
    const qrRect = GCalc.centerRect(r2);
    const keyRect = new PdfRect(
      rect.x + margin2,
      rect.y + margin2,
      rect.width / 2,
      rect.height / 5
    );
    const icon = new PdfRect(
      rect.x + margin2,
      rect.bottom - rect.height / 5 - margin2,
      rect.height / 5,
      rect.height / 5
    );

    p.drawRectangle(GUtil.cutPen, null, rect);

    let {value} = items[i];
    if (getString) {
      value = getString(items[i]);
    }

    p.drawString(value, f, PdfBrush.Black, null, r1, ETextAlign.Center);

    if (hasKey) {
      p.drawString(
        items[i].key,
        GUtil.baseFont,
        PdfBrush.Black,
        null,
        keyRect,
        ETextAlign.CenterLeft
      );
    }

    if (hasStartStop) {
      if (i === 0) p.drawStart(icon, BLUE_COLOR);
      else if (i === total - 1) p.drawStop(icon, BLUE_COLOR);
    }

    const url = urlQr(getQrData(items[i], i, total), siteUrl);
    p.drawQr(qrRect, PdfBrush.Black, url);
  }
}

// ---------------------------------------------------------------------------
// Card layout (QrCards equivalent)
// 2×5 grid; key in top-left, value in bottom-left, QR centered in right half
// ---------------------------------------------------------------------------
interface Card {
  key: string;
  value: string;
  data: object;
}

function drawCards(
  pdf: PdfGen,
  items: { key: string; value: string }[],
  hasKey: boolean,
  getCards: (
    item: { key: string; value: string },
    idx: number,
    total: number,
    rnd: { next(a: number, b: number): number; nextDouble(): number }
  ) => Card[],
  rnd: { next(a: number, b: number): number; nextDouble(): number },
  siteUrl?: string
): void {
  const margin1 = PdfUnit.FromMillimeters(5);
  const margin2 = PdfUnit.FromMillimeters(2);
  const cols = 2,
    rows = 5;
  const { origin, size } = calcOrigin(cols, rows, margin1, PdfSize.A4);
  const total = items.length;

  // Expand items to cards
  const allCards: Card[] = [];
  for (let i = 0; i < total; i++) {
    allCards.push(...getCards(items[i], i, total, rnd));
  }

  let currentPage: ReturnType<typeof pdf.addPage> | null = null;
  let f = GUtil.baseFont;

  for (let i = 0; i < allCards.length; i++) {
    const pos = i % (cols * rows);
    const coord = { x: pos % cols, y: Math.floor(pos / cols) };

    if (pos === 0) {
      currentPage = pdf.addPage();
      f = currentPage.adjustFont(
        size.width / 2,
        allCards.map((c) => c.value),
        GUtil.baseFont
      );
    }
    const p = currentPage!;
    const card = allCards[i];

    const rect = new PdfRect(
      origin.x + coord.x * size.width,
      origin.y + coord.y * size.height,
      size.width,
      size.height
    );
    const rect1 = new PdfRect(
      rect.x + margin2,
      rect.y + margin2,
      rect.width - 2 * margin2,
      rect.height - 2 * margin2
    );
    const key = new PdfRect(rect1.x, rect1.y, rect1.width / 2, rect1.height / 2);
    const value = new PdfRect(rect1.x, key.bottom, key.width, key.height);
    const qr = GCalc.centerRect(new PdfRect(key.right, rect1.y, rect1.width / 2, rect1.height));

    p.drawRectangle(GUtil.cutPen, null, rect);

    if (hasKey && card.key) {
      p.drawString(card.key, GUtil.baseFont, PdfBrush.Black, null, key, ETextAlign.Center);
    }

    p.drawString(card.value, f, PdfBrush.Black, null, value, ETextAlign.Center);
    p.drawQr(qr, PdfBrush.Black, urlQr(card.data, siteUrl));
  }
}

// ===========================================================================
// Strip generators
// ===========================================================================

/** Order.QR_Hra__Hunt — answer hunt, no key displayed */
export class QR_Hra__Hunt implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const id = genId(opts.data.random);
    drawStrip(
      pdf,
      items,
      false,
      false,
      (item, idx, total) => {
        const answer = item.value;
        const rem = removeDiacritics(answer);
        const answers = [...new Set([answer, rem, rem.toLowerCase().trim()])];
        const isNumeric = /^\s*-?\d+(\.\d+)?\s*$/.test(answer);
        return { a: 'a', id, i: idx + 1, c: total, t: isNumeric ? 'n' : 't', o: answers };
      },
      SITE_URL,
      (item) => item.key
    );
  }
}

/** Order.QR_Hra__Path — path game, no key */
export class QR_Hra__Path implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const id = genId(opts.data.random);
    drawStrip(
      pdf,
      items,
      false,
      true,
      (item, idx, total) => {
        return { a: 'm', id, i: idx + 1, c: total, o: item.value };
      },
      SITE_URL
    );
  }
}

/** Order.QR_Hra__Path_Poradi — path game, key shown */
export class QR_Hra__Path_Poradi implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const id = genId(opts.data.random);
    drawStrip(
      pdf,
      items,
      true,
      true,
      (item, idx, total) => {
        return { a: 'm', id, i: idx + 1, c: total, o: item.value };
      },
      SITE_URL,
      (item) => item.key
    );
  }
}

/** Order.QR_Hra__Seek — seek game, key shown */
export class QR_Hra__Seek implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const id = genId(opts.data.random);
    drawStrip(
      pdf,
      items,
      true,
      false,
      (item, idx, total) => {
        return { a: 'f', id, i: idx + 1, c: total, o: item.value };
      },
      SITE_URL
    );
  }
}

/** Order.QR_Hra__Trail — trail game (current + next answer), no key */
export class QR_Hra__Trail implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const id = genId(opts.data.random);
    drawStrip(
      pdf,
      items,
      false,
      false,
      (item, idx, total) => {
        const nextValue = items[(idx + 1) % total].value;
        return { a: 'e', id, i: idx + 1, c: total, o: item.value, o1: nextValue };
      },
      SITE_URL
    );
  }
}

// ===========================================================================
// Card generators
// ===========================================================================

/** Order.QR_Hra__Check — 2 cards per item (correct + 1 wrong), key shown */
export class QR_Hra__Check implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const rnd = opts.data.random;
    const id = genId(rnd);
    drawCards(
      pdf,
      items,
      true,
      (item, idx, total, r) => {
        const others = items.filter((_, j) => j !== idx);
        const shuffled = others.slice().sort(() => r.nextDouble() - 0.5);
        const cards: Card[] = [
          {
            key: item.value,
            value: item.key,
            data: { a: 'c', id, i: idx + 1, c: total, o: 1, rnd: r.next(0, 10) },
          },
          {
            key: item.value,
            value: shuffled[0]?.key ?? '',
            data: { a: 'c', id, i: idx + 1, c: total, o: 0, rnd: r.next(0, 10) },
          },
        ];
        return cards.sort(() => r.nextDouble() - 0.5);
      },
      rnd,
      SITE_URL
    );
  }
}

/** Order.QR_Hra__Pairs — 2 cards per item (key card + value card) */
export class QR_Hra__Pairs implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getItems(opts);
    const rnd = opts.data.random;
    const id = genId(rnd);
    drawCards(
      pdf,
      items,
      false,
      (item, idx, total) => {
        return [
          { key: '', value: item.key, data: { a: 'd', id, i: idx + 1, c: total, g: 2, v: 1 } },
          { key: '', value: item.value, data: { a: 'd', id, i: idx + 1, c: total, g: 2, v: 2 } },
        ];
      },
      rnd,
      SITE_URL
    );
  }
}
